import { Box, Button, CircularProgress, Stack, Typography } from "@mui/joy";
import CustomModal from "components/common/CustomModal";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import pcAndMobileImg from "static/image/payment/pc_and_mobile.png";
import dividerImg from "static/image/payment/divider.svg";
import coachingTimeImg from "static/image/payment/coaching_time.svg";
import coachingRoutine1Img from "static/image/payment/coaching_routine_1.svg";
import coachingRoutine2Img from "static/image/payment/coaching_routine_2.svg";
import coachingRoutine3Img from "static/image/payment/coaching_routine_3.svg";
import coachingCount1Img from "static/image/payment/coaching_count_1.svg";
import coachingCount2Img from "static/image/payment/coaching_count_2.svg";
import coachingCount3Img from "static/image/payment/coaching_count_3.svg";
import paybackMarkImg from "static/image/onboarding/payback_guarantee.svg";
import calendarImg from "static/image/payment/calendar.svg";
import chart100Img from "static/image/payment/chart_100.svg";
import sponsorImg from "static/image/payment/sponsor_payment.png";
import useGetDeviceInfo from "hooks/useGetDeviceInfo";
import useSafeAreaInsets from "hooks/useSafeAreaInsets";
import useGetUrls from "hooks/useGetUrls";
import { sendMessage } from "utils/webview";
import useIsInappWebview from "hooks/useIsInappWebview";
import { theme } from "styles/theme";
import FloatingArea from "components/common/FloatingArea";
import BackButton from "components/common/BackButton";
import { extractHexColor } from "../../../styles/hexOpacity";
import { useRecoilState } from "recoil";
import { paymentOrderAtom } from "../../../recoil/paymentOrderAtom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  PAYMENT_COACH_SEAT_COUNT_ENDPOINT,
  PAYMENT_DOMAIN,
  PAYMENT_ORDER_DETAIL_ENDPOINT,
  paymentApi,
} from "api/paymentApi";
import { userAtom } from "../../../recoil/userAtom";
import CustomTypography from "../../common/CustomTypography";
import { onboardingApi } from "../../../api/onboardingApi";
import CustomTypographyHighlight from "../../common/CustomTypographyHighlight";
import ContentTemplateV2 from "../content/ContentTemplateV2";
import UserInfo from "../../user/UserInfo";
import CxButton from "../../sidebar/CXButton";
import LinkV2 from "../../common/LinkV2";
import { USER_DETAIL_ENDPOINT, USER_DOMAIN, userApi } from "api/userApi";
import { axiosInstance } from "../../../api";
import rocketImg from "../../../static/image/payment/rocket.svg";
import mediImg from "../../../static/image/payment/icon_medi.svg";
import pillImg from "../../../static/image/payment/icon_pill.svg";
import strongImg from "../../../static/image/payment/icon_strong.svg";
import mediRef1Img from "../../../static/image/payment/medi_ref_1.png";
import mediRef2Img from "../../../static/image/payment/medi_ref_2.png";
import mediRef4Img from "../../../static/image/payment/medi_ref_4.png";
import focusCoachingImg from "../../../static/image/onboarding/focus_coaching.svg";
import taskUpdateMechanism from "../../../static/image/payment/task_update_mechanism.png";
import useMixpanelWeb from "../../../hooks/useMixpanelWeb";
import usePaymentPageViewTracking from "../../../hooks/usePaymentPageViewTracking";
import CheckIcon from "../../icons/CheckIcon";
import InfoIcon from "../../icons/InfoIcon";
import PaymentButtonWithPlanSelection from "./elements/PaymentButtonWithPlanSelection";
import FAQTypeC from "./elements/FAQTypeC";
import mixpanel from "mixpanel-browser";
import {
  applyReferralDiscount,
  applyMultiPlanPrices,
  applyPaymentPageType,
  applyPaymentPageTypeSTags,
  applyPromotionPrices,
  applyQMasterFeatures,
  getDedicatedPaymentBannerLabel,
  getSinglePlanPrice,
  isAlertTag,
  isSinglePlanPricingModel,
  PlanItem,
  PlanKey,
} from "./planTypes";
import useReferralDiscount from "../../../hooks/useReferralDiscount";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import ReviewSection from "./elements/ReviewSection";
import DoctorsSection from "./elements/DoctorsSection";
import CloseButtonSmall from "../change-status-button/CloseButtonSmall";
import FAQTypeCPricingModelEG from "./elements/FAQTypeCPricingModelEG";
import FAQRefundTypeDPricingModelEG from "./elements/FAQRefundTypeDPricingModelEG";
import FAQDefault from "./elements/FAQDefault";
import FAQRefundTypeDSinglePricingModel from "./elements/FAQRefundTypeDSinglePricingModel";
import FAQRefundTypeB from "./elements/FAQRefundTypeB";

dayjs.extend(utc);
dayjs.extend(timezone);

// feature.key 는 안정 식별자(INFO_CONTENTS / refundLabel 분기). text 는 카피로 자유 갱신.
// "payback" key 는 렌더 시 refundLabel(효과 보장 페이백 / 14일 내 전액 환불 보장) 로 동적 치환됨.
const plans: PlanItem[] = [
  {
    key: "starter",
    name: "스타터",
    price: 295000,
    features: [
      {
        key: "coaching_starter",
        text: "최대 주 2회 코칭",
        included: true,
        info: true,
      },
      { key: "payback", text: "환불 페이백", included: true },
    ],
  },
  {
    key: "challenger",
    name: "챌린저",
    price: 395000,
    tag: "가장 인기 있는",
    features: [
      {
        key: "coaching_challenger",
        text: "2배 더 많은 코칭 (스타터 대비)",
        included: true,
        info: true,
      },
      {
        key: "regular_check",
        text: "정기 검사",
        included: true,
        info: true,
      },
      { key: "payback", text: "환불 페이백", included: true },
    ],
  },
  {
    key: "master",
    name: "마스터",
    price: 465000,
    tag: "가장 밀도 있는",
    features: [
      {
        key: "coaching_master",
        text: "2배 더 많은 코칭 (챌린저 대비)",
        included: true,
        info: true,
      },
      {
        key: "coaching_master_speed",
        text: "5시간 더 빠른 코칭 (챌린저 대비)",
        included: true,
        info: true,
      },
      {
        key: "regular_check",
        text: "정기 검사",
        included: true,
        info: true,
      },
      { key: "payback", text: "환불 페이백", included: true },
    ],
  },
];

const formatCurrency = (n: number) => n.toLocaleString("ko-KR");

export default function PaymentContentDefault({
  isFromOnboarding = false,
  enableCloseButton = false,
}: {
  isFromOnboarding?: boolean;
  enableCloseButton?: boolean;
}) {
  const floatingAreaRef = useRef<HTMLDivElement>(null);
  const planSectionRef = useRef<HTMLDivElement>(null); // 플랜 섹션 ref
  const navigate = useNavigate();

  const isInappWebview = useIsInappWebview();
  const { appVersion } = useGetDeviceInfo();

  const paddingX = 4.5;

  const [user, setUser] = useRecoilState(userAtom);
  const isPaybackPolicy =
    user?.refundPolicyType !== "c" && user?.refundPolicyType !== "b";

  const refundLabel = isPaybackPolicy
    ? "효과 보장 페이백"
    : "14일 내 전액 환불 보장";
  const guaranteeLabel = isPaybackPolicy
    ? "효과 보장 페이백"
    : "14일 전액 환불 보장";

  const [paymentOrderState, setPaymentOrderState] =
    useRecoilState(paymentOrderAtom);

  // 레퍼럴 결제 할인(챌린저/마스터 -30,000) + 카운트다운. 만료 시각은 order/detail 이 내려줌
  // (affiliate: 결제페이지 최초 진입 후 24h, 단 어트리뷰션 윈도우 마감이 더 이르면 그 시각으로 캡 /
  //  discount: 코드 종료 일시(endAt) 고정).
  const {
    active: referralDiscountActive,
    hoursLeft: referralHoursLeft,
    expiresToday: referralExpiresToday,
  } = useReferralDiscount(paymentOrderState?.affiliateDiscountExpiresAt);
  // discount 유형은 카운트다운 대신 정원 소진 안내 배너를 노출한다 (크리에이터 정산 무관, 조기 마감형 문구).
  // 단, 오늘이 만료일(코드 endAt)이면 affiliate 와 같은 남은 시간 카운트다운으로 통일한다.
  const isDiscountReferral = user?.referralCodeType === "discount";
  const discountBannerLabel = referralExpiresToday
    ? `종료까지 ${referralHoursLeft}시간 남음`
    : "정원 소진 시 조기 종료";
  // 배너 표시용 코드 이름(코드 아님). 없으면 코드로 폴백, 둘 다 없으면 이름 없이 안내만.
  const referralDisplayName =
    user?.affiliateReferralName || user?.affiliateReferralCode;

  const isFirstOrder = paymentOrderState?.isFirst;
  const fullWidth = isFirstOrder ? true : !!isInappWebview;

  const isSinglePlanModel = isSinglePlanPricingModel(user?.pricingModel);
  const isNoStarterModel = user?.pricingModel === "eg";
  const isPaymentPageTypeQ = user?.paymentPageType === "q";
  const isPaymentPageTypeS = user?.paymentPageType === "s";

  // 제휴 추천 대상 유저(affiliateReferralCode 보유). 진입 시 stale order 로
  // 상단 띠/할인가가 빠진 화면이 먼저 그려졌다가 뒤늦게 뜨는 것을 막는 분기에 사용.
  const isReferral = !!user?.affiliateReferralCode;

  // e/h/i 모델은 challenger에 매핑되는 단일 "맞춤 플랜" 카드만 노출한다.
  const singleCustomPlan: PlanItem = {
    key: "challenger",
    name: "맞춤 플랜",
    price: getSinglePlanPrice(user?.pricingModel) ?? 395000,
    features: [
      {
        key: "coaching_single",
        text: "1:1 전담 코칭",
        included: true,
        info: true,
      },
      {
        key: "regular_check",
        text: "정기 검사",
        included: true,
        info: true,
      },
      { key: "payback", text: "환불 페이백", included: true },
    ],
  };

  const [countCoachSeatRemain, setCountCoachSeatRemain] = useState<
    number | undefined
  >(undefined);

  // 제휴 유저 한정: 이번 진입에서 order/detail 최신 응답을 받았는지(=신선) 추적.
  // 신선 응답에는 affiliateDiscountExpiresAt 가 항상 포함되므로, 그 전까지 로딩을 유지해
  // stale atom 으로 띠/할인가가 빠진 화면을 먼저 그리지 않도록 한다.
  const [isOrderFreshlyLoaded, setIsOrderFreshlyLoaded] = useState(false);

  const handleBack = () => {
    if (user?.onboardingType === "g") {
      navigate("/program-setup-3/product/1", { replace: true });
    } else if (user?.paymentPageType === "m") {
      if (user?.onboardingType === "f") {
        navigate("/onboarding_new/introduction/6", { replace: true });
      } else {
        navigate("/onboarding_new/curriculum/1", { replace: true });
      }
    } else {
      navigate("/onboarding_new/curriculum/1", { replace: true });
    }
  };

  useQuery(
    [PAYMENT_DOMAIN, PAYMENT_ORDER_DETAIL_ENDPOINT, user?.userId],
    () => paymentApi.getOrderDetail(),
    {
      enabled:
        !!axiosInstance.defaults.headers["X-Auth-Token"] && !!user?.accessToken,
      onSuccess: (data) => {
        if (data && data.result) {
          setPaymentOrderState(data.result);
          setIsOrderFreshlyLoaded(true);
        }
      },
    }
  );

  useQuery(
    [PAYMENT_DOMAIN, PAYMENT_COACH_SEAT_COUNT_ENDPOINT],
    () => paymentApi.countRemainCoachSeat(),
    {
      enabled: true,
      refetchInterval: 5000,
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: true,
      onSuccess: (data) => {
        if (data && data.result) {
          // Always update, including 0, so UI reflects real-time count
          setCountCoachSeatRemain(data.result.countRemainCoachSeat ?? 0);
        } else {
          setCountCoachSeatRemain(undefined);
        }
      },
    }
  );

  // Fetch latest user detail to ensure subscription state is up-to-date before firing Mixpanel
  useQuery(
    [USER_DOMAIN, USER_DETAIL_ENDPOINT],
    useCallback(() => userApi.getDetail(), []),
    {
      enabled:
        !!axiosInstance.defaults.headers["X-Auth-Token"] && !!user?.accessToken,
      onSuccess: (data) => {
        if (data && data.result) {
          setUser((user) => ({
            ...user,
            ...data.result,
          }));
        }
      },
    }
  );

  const pageKey = "payment_1";
  const SCROLL_CONTAINER_ID = "payment-scroll-container";

  const { mutate: savePage } = useMutation(
    (pageId: string) => onboardingApi.savePage(pageId),
    {
      onSuccess: () => {},
    }
  );

  const setMixPanel = useMixpanelWeb({
    eventName: "결제 페이지 진입",
    params: {
      event_source: "app",
    },
  });

  // 공통 페이지 진입 트래킹 (Meta/Firebase/Appsflyer/Mixpanel)
  usePaymentPageViewTracking({
    user,
    isInappWebview,
    appVersion,
    setMixPanel,
  });

  function useEffectOnce(callback: () => void) {
    const ref = useRef(false);

    useEffect(() => {
      if (ref.current) return;
      ref.current = true;

      if (typeof callback === "function") {
        callback();
      }
    }, []);
  }

  useEffectOnce(() => {
    savePage(pageKey);
  });

  const { companyInfoUrl, privacyPolicyUrl, servicePolicyUrl } = useGetUrls();
  const hasSentSafeAreaColorRef = useRef(false);

  useEffect(() => {
    if (hasSentSafeAreaColorRef.current) return;
    if (!(isInappWebview && paymentOrderState)) return;

    const topBackground = extractHexColor(theme.vars.palette.Background.Level3);
    const bottomBackground = extractHexColor(
      theme.vars.palette.Background.Level1
    );

    if (topBackground && bottomBackground) {
      sendMessage(
        JSON.stringify({
          event: "set_safearea_color",
          params: {
            topColor: `${topBackground}`,
            bottomColor: `${bottomBackground}`,
          },
        })
      );
      hasSentSafeAreaColorRef.current = true;
    }
  }, [isInappWebview, paymentOrderState]);

  const { bottom: safeBottom } = useSafeAreaInsets();
  const [containerHeight, setContainerHeight] = useState<number>(
    window.innerHeight - safeBottom
  );

  /* -----------------------------
     신규: 선택된 플랜 상태/헬퍼
  ------------------------------ */
  // 저장 키를 paymentPageType 별로 분리 (q 의 마스터 기본값이 p 등 다른 타입으로 새지 않도록)
  const SESSION_SELECTED_PLAN_KEY = `payment.selectedPlan.100dayABC.withCommitment.${
    user?.paymentPageType ?? "default"
  }`;
  const [selectedPlan, setSelectedPlan] = useState<PlanKey>(() => {
    // q/s 결제 페이지는 저장값과 무관하게 항상 마스터를 기본 선택
    if (isPaymentPageTypeQ || isPaymentPageTypeS) return "master";
    try {
      if (typeof window !== "undefined") {
        const saved = sessionStorage.getItem(SESSION_SELECTED_PLAN_KEY);
        if (
          saved &&
          (saved === "starter" || saved === "challenger" || saved === "master")
        ) {
          return saved as PlanKey;
        }
      }
    } catch {}
    return "challenger";
  });
  useEffect(() => {
    try {
      if (typeof window !== "undefined") {
        sessionStorage.setItem(SESSION_SELECTED_PLAN_KEY, selectedPlan);
      }
    } catch {}
  }, [selectedPlan]);
  const dedicatedBannerLabel = getDedicatedPaymentBannerLabel(user?.promotion);
  const effectivePlans = applyReferralDiscount(
    applyPaymentPageTypeSTags(
      applyQMasterFeatures(
        applyPaymentPageType(
          applyPromotionPrices(
            applyMultiPlanPrices(plans, user?.pricingModel),
            user?.promotion
          ),
          user?.paymentPageType
        ),
        user?.paymentPageType
      ),
      user?.paymentPageType
    ),
    referralDiscountActive
  );
  const visiblePlans = isSinglePlanModel
    ? [singleCustomPlan]
    : isNoStarterModel
      ? effectivePlans.filter((plan) => plan.key !== "starter")
      : effectivePlans;

  const effectiveSelectedPlan: PlanKey = isSinglePlanModel
    ? "challenger"
    : isNoStarterModel && selectedPlan === "starter"
      ? "challenger"
      : selectedPlan;

  useEffect(() => {
    if (selectedPlan !== effectiveSelectedPlan) {
      setSelectedPlan(effectiveSelectedPlan);
    }
  }, [effectiveSelectedPlan, selectedPlan]);

  const selectedPlanObj =
    visiblePlans.find((p) => p.key === effectiveSelectedPlan) ??
    visiblePlans[0]!;

  const planTitleRef = useRef<HTMLDivElement>(null); // title anchor

  type InfoModalData = { title: string; subtitle: string; action: string };

  // feature.key 기준. text 카피가 바뀌어도 여기 영향 없음.
  const INFO_CONTENTS: Record<string, InfoModalData> = {
    coaching_single: {
      title: "1:1 전담 코칭",
      subtitle: "하루 최대 1회, 코치의 피드백과 함께 맞춤 활동지가 제공됩니다.",
      action: "확인",
    },
    coaching_starter: {
      title: "기본 코칭",
      subtitle:
        "일주일에 최대 2회, 코치의 피드백과 함께 맞춤 활동지가 제공됩니다. 프로그램 진도도 매주 최대 2회까지 나가게 됩니다.",
      action: "확인",
    },
    coaching_challenger: {
      title: "집중 코칭",
      subtitle:
        "하루 최대 1회, 코치의 피드백과 함께 맞춤 활동지가 제공됩니다. 프로그램 진도도 매일 최대 1회까지 나가게 됩니다.",
      action: "확인",
    },
    coaching_master: {
      title: "프리미엄 코칭",
      subtitle:
        "하루 최대 2회, 코치의 피드백과 함께 맞춤 활동지가 제공됩니다. 프로그램 진도도 매일 최대 2회까지 나가게 됩니다.",
      action: "확인",
    },
    coaching_master_speed: {
      title: "프리미엄 코칭",
      subtitle:
        "스타터와 챌린저 플랜 대비 평균 5시간 더 빨리 코칭을 받을 수 있습니다.",
      action: "확인",
    },
    lead_coach: {
      title: "수석 코치",
      subtitle:
        "오랜 기간 다양한 사례를 성공적으로 이끌어온 상위 10%의 수석 코치가 배정됩니다.",
      action: "확인",
    },
    coaching_fit: {
      title: "코칭 핏 보장",
      subtitle:
        "배정된 담당 코치와의 합이 만족스럽지 않다면, 시작 후 14일 이내 1회 다른 수석 코치로 변경을 도와드립니다.",
      action: "확인",
    },
    flexible_holding: {
      title: "플렉서블 홀딩",
      subtitle:
        "100일의 프로그램 기간 중, 최대 60일간 총 3회에 걸친 자유로운 일시정지를 지원합니다.",
      action: "확인",
    },
    regular_check: {
      title: "정기 검사",
      subtitle: "정신건강 주요 항목을 2주마다 정기적으로 측정합니다.",
      action: "확인",
    },
  };

  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [infoModal, setInfoModal] = useState<InfoModalData>({
    title: "",
    subtitle: "",
    action: "확인",
  });

  const handleOpenInfo = (feature: { key: string; text: string }) => {
    const next = INFO_CONTENTS[feature.key] || {
      title: feature.text,
      subtitle: "자세한 내용은 고객센터로 문의해주세요.",
      action: "확인",
    };
    setInfoModal(next);
    setInfoModalOpen(true);
  };

  const handleOpenInfoCoachSeat = (planName: string) => {
    // 단일 플랜(맞춤 플랜)은 플랜명 노출 없이 일반 카피 사용
    const tail = isSinglePlanModel
      ? "현재 정원 마감이 임박하였습니다."
      : `현재 ${planName} 플랜의 정원 마감이 임박하였습니다.`;
    setInfoModal({
      title: "정원 제한",
      subtitle: `최상의 프로그램 진행 경험을 위해, 코치별 담당 인원을 제한하고 있습니다. ${tail}`,
      action: "확인",
    });
    setInfoModalOpen(true);
  };

  useEffect(() => {
    mixpanel.start_session_recording();

    return () => {
      mixpanel.stop_session_recording();
    };
  }, []);

  // 제휴 유저인데 아직 활성 띠 정보가 없고(stale 이거나 미수신) 이번 진입의 신선 응답도
  // 받지 못했다면, 띠/할인가가 정확해질 때까지 기존 로딩 스피너를 유지한다.
  // (낙관적 노출 대신 "확인 후 정확히 표시" — 활성 윈도우면 신선 응답 직후 띠가 바로 뜬다.)
  if (
    !paymentOrderState ||
    (isReferral && !referralDiscountActive && !isOrderFreshlyLoaded)
  ) {
    return (
      <Stack sx={{ height: "100vh", width: "100%", overflow: "hidden" }}>
        {
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              width: "100%",
              px: "8px",
              height: "60px",
              flexShrink: 0,
              backgroundColor: "transparent",
              position: "absolute",
              top: 0,
              left: 0,
              zIndex: 10,
            }}
          >
            {isFromOnboarding && (
              <BackButton
                onBack={handleBack}
                iconColor={theme.vars.palette.Content.Negative}
              />
            )}
            {enableCloseButton && (
              <CloseButtonSmall
                iconColor={theme.vars.palette.Content.Negative}
              />
            )}
          </Stack>
        }
        <ContentTemplateV2
          fullBleed
          disableBottomSafeArea
          isWide
          setContainerHeight={setContainerHeight}
          containerId={SCROLL_CONTAINER_ID}
        >
          <Stack
            sx={{ width: "100%", minHeight: containerHeight }}
            alignItems="center"
            justifyContent="center"
          >
            <CircularProgress color="neutral" />
          </Stack>
        </ContentTemplateV2>
      </Stack>
    );
  }

  // 플랜 선택 섹션
  const planSelectionSection = (
    <Stack
      ref={planSectionRef}
      justifyContent="center"
      alignItems={"center"}
      sx={{
        backgroundColor: theme.vars.palette.Surface.Highlight,
        width: "100%",
        maxWidth: "100%",
        mx: "auto",
        px: paddingX,
        pt: "40px",
        pb: 10,
      }}
    >
      <Stack
        sx={{
          width: "100%",
          maxWidth:
            isInappWebview || isNoStarterModel || isSinglePlanModel
              ? "660px"
              : "1000px",
        }}
        spacing={5}
        alignItems="center"
      >
        <Box ref={planTitleRef} />

        <Stack spacing={"16px"}>
          <CustomTypography
            variant={"H2"}
            sx={{
              color: theme.vars.palette.Content.Negative,
              fontWeight: 700,
              textAlign: "center",
            }}
          >
            {isSinglePlanModel && isFirstOrder
              ? `${user?.nickname ? `${user?.nickname}님, 100일 완성\n플랜을 시작하세요` : "100일 완성\n플랜을 시작하세요"}`
              : isFirstOrder
                ? `${user?.nickname ? `${user?.nickname}님, 100일 완성\n플랜을 선택하세요` : "100일 완성\n플랜을 선택하세요"}`
                : "플랜을 선택하세요"}
          </CustomTypography>
        </Stack>

        <Box
          sx={{
            width: "100%",
            display: "grid",
            gridTemplateColumns:
              isInappWebview || isSinglePlanModel
                ? "1fr"
                : `repeat(${visiblePlans.length}, minmax(0, 1fr))`,
            gap: "12px",
          }}
        >
          {visiblePlans.map((plan) => {
            const selected = effectiveSelectedPlan === plan.key;
            const showCapacityRow =
              !!countCoachSeatRemain &&
              countCoachSeatRemain > 0 &&
              plan.key !== "starter" &&
              isFirstOrder &&
              !(isPaymentPageTypeQ && plan.key === "challenger");
            const showTag =
              !!plan.tag && !(isPaymentPageTypeQ && plan.key === "challenger");
            const titleBlock = (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-start"
              >
                <Stack spacing={0.5} sx={{ width: "100%" }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ width: "100%", pl: 0.5 }}
                  >
                    <CustomTypography
                      variant="Body"
                      sx={{
                        color: theme.vars.palette.Content.Highlight,
                        fontWeight: 700,
                      }}
                    >
                      {plan.name}
                    </CustomTypography>
                    {showTag && (
                      <Box
                        sx={{
                          height: "20px",
                          borderRadius: "4px",
                          px: "4px",
                          display: "flex",
                          alignItems: "center",
                          backgroundColor: isAlertTag(plan.tag)
                            ? theme.vars.palette.Surface.SoftHighlightWithAlert
                            : theme.vars.palette.Surface.SoftHighlight,
                        }}
                      >
                        <Typography
                          variant="Footnote"
                          sx={{
                            color: isAlertTag(plan.tag)
                              ? theme.vars.palette.Content.HighlightWithAlert
                              : theme.vars.palette.Content.Highlight,
                            fontWeight: 700,
                          }}
                        >
                          {plan.tag}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                  <CustomTypography
                    variant="H3"
                    sx={{
                      color: theme.vars.palette.Content.Strong,
                      fontWeight: 700,
                    }}
                  >
                    {`${formatCurrency(plan.price)}원`}
                    {plan.originalPrice != null && (
                      <CustomTypography
                        component="span"
                        variant="Body"
                        sx={{
                          ml: 0.5,
                          color: theme.vars.palette.Content.Medium,
                          fontWeight: 500,
                          textDecoration: "line-through",
                        }}
                      >
                        {`${formatCurrency(plan.originalPrice)}원`}
                      </CustomTypography>
                    )}
                    <CustomTypography
                      component="span"
                      variant="Body"
                      sx={{
                        ml: 0.5,
                        color: theme.vars.palette.Content.Medium,
                        fontWeight: 500,
                      }}
                    >
                      {"/ 100일"}
                    </CustomTypography>
                  </CustomTypography>
                </Stack>
              </Stack>
            );

            const selectButton = (
              <Button
                fullWidth
                size="md"
                variant={selected ? "Outlined" : "Soft"}
                onClick={() => setSelectedPlan(plan.key)}
                sx={{ borderRadius: "10px", fontWeight: 700 }}
              >
                {selected ? (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <CheckIcon ariaLabel="selected" />
                    <span>선택됨</span>
                  </span>
                ) : (
                  "선택하기"
                )}
              </Button>
            );

            const capacityRow = showCapacityRow ? (
              <Stack
                direction="row"
                spacing={"4px"}
                sx={{
                  mt: isSinglePlanModel ? "20px !important" : "8px !important",
                  width: "100%",
                  justifyContent: isSinglePlanModel
                    ? "space-between"
                    : "center",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant={"Caption"}
                  sx={{
                    px: isSinglePlanModel ? "4px" : "0px",
                    color: theme.vars.palette.Content.Medium,
                    fontWeight: 500,
                  }}
                >
                  {"현재 신청 가능 인원:"}
                  <Typography
                    variant={"Caption"}
                    sx={{
                      pr: 0,
                      color: theme.vars.palette.Content.HighlightWithAlert,
                      fontWeight: 500,
                    }}
                  >
                    {plan.key === "challenger" ||
                    (isPaymentPageTypeQ && plan.key === "master")
                      ? countCoachSeatRemain + 1
                      : countCoachSeatRemain}
                    명
                  </Typography>
                </Typography>
                <Box
                  role="button"
                  tabIndex={0}
                  aria-label={`정원 제한 자세히 보기`}
                  onClick={() => handleOpenInfoCoachSeat(plan.name)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleOpenInfoCoachSeat(plan.name);
                    }
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                >
                  <InfoIcon ariaLabel="info" />
                </Box>
              </Stack>
            ) : null;

            const featuresList = (
              <Stack spacing={1}>
                {(isFirstOrder
                  ? plan.features
                  : plan.features.filter((feat) => !feat.text.includes("환불"))
                ).map((f, idx) => (
                  <Stack
                    key={`${plan.key}-${idx}`}
                    direction="row"
                    alignItems="flex-start"
                    justifyContent="space-between"
                    sx={{ width: "100%" }}
                  >
                    <Stack
                      direction="row"
                      alignItems="flex-start"
                      spacing={0.5}
                    >
                      <CheckIcon
                        ariaLabel="included"
                        style={{ marginTop: "2px" }}
                      />
                      <Typography
                        variant="Body"
                        sx={{
                          px: 0,
                          display: "flex",
                          alignItems: "center",
                          color: theme.vars.palette.Content.Strong,
                          fontWeight: 500,
                        }}
                      >
                        {f.key === "payback" ? refundLabel : f.text}
                      </Typography>
                    </Stack>
                    {f.info && (
                      <Box
                        role="button"
                        tabIndex={0}
                        aria-label={`${f.text} 자세히 보기`}
                        onClick={() => handleOpenInfo(f)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleOpenInfo(f);
                          }
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                        }}
                      >
                        <InfoIcon ariaLabel="info" />
                      </Box>
                    )}
                  </Stack>
                ))}
              </Stack>
            );

            const cardDivider = (
              <Box
                sx={{
                  width: "100%",
                  height: "1px",
                  backgroundColor: theme.vars.palette.Background.Level3,
                }}
              />
            );

            return (
              <Box
                key={plan.key}
                sx={{
                  borderRadius: "16px",
                  backgroundColor: theme.vars.palette.Surface.Forward,
                  px: "24px",
                  py: "28px",
                }}
              >
                <Stack spacing={"20px"}>
                  {titleBlock}

                  {isSinglePlanModel ? (
                    <>
                      {cardDivider}
                      {featuresList}
                      {capacityRow && cardDivider}
                      {capacityRow}
                    </>
                  ) : (
                    <>
                      {selectButton}
                      {capacityRow}
                      {featuresList}
                    </>
                  )}
                </Stack>
              </Box>
            );
          })}
        </Box>
      </Stack>
    </Stack>
  );

  return (
    <Stack sx={{ height: "100vh", width: "100%", overflow: "hidden" }}>
      {
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            width: "100%",
            px: "8px",
            height: "60px",
            flexShrink: 0,
            backgroundColor: "transparent",
            position: "absolute",
            top: dedicatedBannerLabel || referralDiscountActive ? "42px" : 0, // 전용/제휴 띠(42px) 아래로 헤더 이동
            left: 0,
            zIndex: 10,
          }}
        >
          {isFromOnboarding && (
            <BackButton
              onBack={handleBack}
              iconColor={theme.vars.palette.Content.Negative}
            />
          )}
          {enableCloseButton && (
            <CloseButtonSmall iconColor={theme.vars.palette.Content.Negative} />
          )}
        </Stack>
      }
      <ContentTemplateV2
        fullBleed
        disableBottomSafeArea
        isWide
        setContainerHeight={setContainerHeight}
        containerId={SCROLL_CONTAINER_ID}
      >
        {containerHeight > 0 && (
          <Stack
            sx={{
              // floating area(~100px) + footer↔floating 시각 여백.
              pb: "200px",
              backgroundColor: theme.vars.palette.Background.Level3,
            }}
          >
            {dedicatedBannerLabel && (
              <Box
                sx={{
                  width: "100%",
                  height: "42px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  px: "24px",
                  background: theme.vars.palette.Surface.SoftHighlight,
                  position: "sticky",
                  top: 0, // 최상단 고정 (헤더보다 위)
                  zIndex: 11,
                }}
              >
                <CustomTypography
                  variant="Caption"
                  sx={{
                    color: theme.vars.palette.Content.Highlight,
                    fontWeight: 500,
                  }}
                >
                  {dedicatedBannerLabel}
                </CustomTypography>
              </Box>
            )}
            {referralDiscountActive && (
              <Box
                sx={{
                  width: "100%",
                  height: "42px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  px: "24px",
                  background: theme.vars.palette.Surface.SoftHighlight,
                  position: "sticky",
                  top: 0, // 최상단 고정 (헤더보다 위)
                  zIndex: 11,
                }}
              >
                <CustomTypography
                  variant="Caption"
                  sx={{
                    color: theme.vars.palette.Content.Highlight,
                    fontWeight: 500,
                  }}
                >
                  {isDiscountReferral
                    ? referralDisplayName
                      ? `[${referralDisplayName} 구독자 전용 혜택] ${discountBannerLabel}`
                      : discountBannerLabel
                    : `전용 우대 혜택 종료까지 ${referralHoursLeft}시간 남음`}
                </CustomTypography>
              </Box>
            )}
            <>{planSelectionSection}</>
            <ReviewSection />
            <DoctorsSection />

            {isFirstOrder && (
              <Stack
                py={10}
                spacing={5}
                alignItems={"center"}
                sx={{
                  backgroundColor: theme.vars.palette.Content.Strong,
                  width: "100%",
                  mx: "auto",
                  px: paddingX,
                }}
              >
                <Stack alignItems="center" spacing={6} sx={{ width: "100%" }}>
                  <CustomTypography
                    variant={"H2"}
                    sx={{
                      color: theme.vars.palette.Content.SoftHighlight,
                      fontWeight: 700,
                      textAlign: "center",
                    }}
                  >
                    {"100일이면\n완성되는 새로운 삶"}
                  </CustomTypography>

                  <Stack
                    direction="column"
                    alignItems="start"
                    sx={{ width: "100%", maxWidth: "660px" }}
                  >
                    <CustomTypographyHighlight
                      variant={"Subtitle"}
                      sx={{
                        color: theme.vars.palette.Content.SuperWeak,
                        fontWeight: 500,
                      }}
                      highlightedText={"1%도 되지 않는"}
                      highlightVariant={"Subtitle"}
                      highlightSx={{
                        color: theme.vars.palette.Content.Negative,
                        fontWeight: 700,
                      }}
                    >
                      {"단 100일. 디스턴싱으로 변화를 만드는 데 걸리는 시간입니다.\n\n" +
                        "앞으로 남은 인생이 50년이라면, 이 100일은 1%도 되지 않는 아주 짧은 시간입니다.\n\n" +
                        "1로 99를 얻는 기회, 지금 시작하세요."}
                    </CustomTypographyHighlight>
                  </Stack>
                </Stack>
              </Stack>
            )}

            {isFirstOrder && isPaybackPolicy && (
              <Stack
                py={10}
                spacing={5}
                justifyContent="center"
                alignItems={"center"}
                sx={{
                  backgroundColor: theme.vars.palette.Background.Level1,
                  width: "100%",
                  px: paddingX,
                  mx: "auto",
                }}
              >
                <Stack
                  spacing={4}
                  alignItems={"center"}
                  justifyContent={"center"}
                >
                  <img
                    src={paybackMarkImg}
                    alt={"empty"}
                    style={{
                      width: "100%",
                      maxWidth: "121px",
                    }}
                  />
                  <CustomTypography
                    variant={"H2"}
                    sx={{
                      color: theme.vars.palette.Content.Strong,
                      fontWeight: 700,
                    }}
                  >
                    {"효과 보장 페이백"}
                  </CustomTypography>
                </Stack>

                <Stack
                  direction="column"
                  alignItems="start"
                  spacing={2}
                  sx={{ width: "100%", maxWidth: "660px" }}
                >
                  <CustomTypographyHighlight
                    variant={"Subtitle"}
                    sx={{
                      color: theme.vars.palette.Content.Medium,
                      fontWeight: 500,
                    }}
                    highlightedText={"비용 전액"}
                    highlightVariant={"Subtitle"}
                    highlightSx={{
                      color: theme.vars.palette.Content.Strong,
                      fontWeight: 700,
                    }}
                  >
                    {"디스턴싱 프로그램의 첫째 주 만족도는 92%. 그러나 100일 뒤 이 만족도는 98%로 더욱 높아집니다.\n\n" +
                      "100일 동안 주 1회 이상 참여한 후에도 변화가 느껴지지 않으신다면 비용 전액을 돌려드립니다.\n\n" +
                      "주 1회 이상 참여하는 게 어려울 것 같나요? 그렇지 않아요. 30분 정도면 충분하고, 전담 코치 선생님이 꾸준히 프로그램에 참여할 수 있도록 도와줄 거랍니다.\n\n" +
                      "시도해서 잃을 것은 없습니다. 시도하지 않으면 변화할 기회조차 얻지 못합니다."}
                  </CustomTypographyHighlight>
                </Stack>
              </Stack>
            )}
            {isFirstOrder && user?.refundPolicyType === "c" && (
              <Stack
                py={10}
                spacing={5}
                justifyContent="center"
                alignItems={"center"}
                sx={{
                  backgroundColor: theme.vars.palette.Background.Level1,
                  width: "100%",
                  px: paddingX,
                  mx: "auto",
                }}
              >
                <Stack
                  spacing={4}
                  alignItems={"center"}
                  justifyContent={"center"}
                >
                  <img
                    src={paybackMarkImg}
                    alt={"empty"}
                    style={{
                      width: "100%",
                      maxWidth: "121px",
                    }}
                  />
                  <CustomTypography
                    variant={"H2"}
                    sx={{
                      color: theme.vars.palette.Content.Strong,
                      fontWeight: 700,
                    }}
                  >
                    {"14일 전액 환불 보장"}
                  </CustomTypography>
                </Stack>

                <Stack
                  direction="column"
                  alignItems="start"
                  spacing={2}
                  sx={{ width: "100%", maxWidth: "660px" }}
                >
                  <CustomTypographyHighlight
                    variant={"Subtitle"}
                    sx={{
                      color: theme.vars.palette.Content.Medium,
                      fontWeight: 500,
                    }}
                    highlightedText={"비용 전액"}
                    highlightVariant={"Subtitle"}
                    highlightSx={{
                      color: theme.vars.palette.Content.Strong,
                      fontWeight: 700,
                    }}
                  >
                    {"디스턴싱 프로그램의 첫째 주 만족도는 92%. 그러나 100일 뒤 이 만족도는 98%로 더욱 높아집니다.\n\n" +
                      "결제일로부터 14일 이내 2세트 이상 제공된 활동지를 진행했음에도 만족스럽지 못하다면, 이유와 관계없이 비용 전액을 환불해드립니다.\n\n" +
                      "시도해서 잃을 것은 없습니다. 시도하지 않으면 변화할 기회조차 얻지 못합니다."}
                  </CustomTypographyHighlight>
                </Stack>
              </Stack>
            )}
            {
              <Stack
                py={10}
                spacing={5}
                justifyContent="center"
                alignItems={"center"}
                sx={{
                  backgroundColor: theme.vars.palette.Background.Level3,
                  width: "100%",
                  mx: "auto",
                  px: paddingX,
                }}
              >
                <Stack
                  direction="column"
                  alignItems="start"
                  spacing={"20px"}
                  sx={{ width: "100%", maxWidth: "660px" }}
                >
                  <img
                    src={mediImg}
                    alt={"empty"}
                    style={{
                      width: "100%",
                      maxWidth: "35px",
                    }}
                  />
                  <CustomTypography
                    variant={"H2"}
                    sx={{
                      p: 0,
                      color: theme.vars.palette.Content.Strong,
                      fontWeight: 700,
                    }}
                  >
                    {"약물 없이\n효과로 말하다"}
                  </CustomTypography>
                </Stack>

                <Stack
                  spacing={3}
                  direction="column"
                  alignItems="start"
                  sx={{ width: "100%", maxWidth: "660px" }}
                >
                  <CustomTypography
                    variant={"Subtitle"}
                    sx={{
                      color: theme.vars.palette.Content.Medium,
                      fontWeight: 500,
                      p: 0,
                    }}
                  >
                    <>
                      {
                        "디스턴싱은 현대 정신의학 이론을 집대성하여 재구성한 전세계 유일의 "
                      }
                      <Typography
                        component="span"
                        variant={"Subtitle"}
                        sx={{
                          p: 0,
                          m: 0,
                          color: theme.vars.palette.Content.Strong,
                          fontWeight: 700,
                        }}
                      >
                        비약물 심리치료
                      </Typography>
                      {"입니다.\n\n" +
                        "디스턴싱은 인지행동치료(CBT, CT-R), 수용전념치료(ACT), 마음챙김기반인지치료(MBCT), 자비중심치료(CFT), 행동활성화(BA), 변증법적행동치료(DBT), 심리도식치료(ST), 합리적정서행동치료(REBT) 중에서 탈중심화(decentering)와 행동주의적 접근을 토대로 개발된 "}
                      <Typography
                        component="span"
                        variant={"Subtitle"}
                        sx={{
                          p: 0,
                          m: 0,
                          color: theme.vars.palette.Content.Strong,
                          fontWeight: 700,
                        }}
                      >
                        인지치료
                      </Typography>
                      {
                        "입니다.\n\n인지치료는 PTSD, 우울증, 불안장애, 공황장애, 강박증, 섭십장애 등 거의 모든 정신 질환에서 "
                      }
                      <Typography
                        component="span"
                        variant={"Subtitle"}
                        sx={{
                          p: 0,
                          m: 0,
                          color: theme.vars.palette.Content.Strong,
                          fontWeight: 700,
                        }}
                      >
                        유의미한 치료 효과
                      </Typography>
                      {"를 증명하였습니다.¹"}
                    </>
                  </CustomTypography>
                </Stack>

                <Stack spacing={5}>
                  <img
                    src={mediRef1Img}
                    alt={"empty"}
                    style={{
                      width: "100%",
                      maxWidth: "660px",
                    }}
                  />
                  <CustomTypography
                    variant={"Caption"}
                    sx={{
                      fontWeight: 500,
                      color: theme.vars.palette.Content.Medium,
                      width: "100%",
                      textAlign: "start",
                    }}
                  >
                    {"¹ Cuijpers et al., 2025 (JAMA Psychiatry)"}
                  </CustomTypography>
                </Stack>
              </Stack>
            }

            {
              <Stack
                py={10}
                spacing={5}
                justifyContent="center"
                alignItems={"center"}
                sx={{
                  backgroundColor: theme.vars.palette.Background.Level1,
                  width: "100%",
                  mx: "auto",
                  px: paddingX,
                }}
              >
                <Stack
                  direction="column"
                  alignItems="start"
                  spacing={"20px"}
                  sx={{ width: "100%", maxWidth: "660px" }}
                >
                  <img
                    src={pillImg}
                    alt={"empty"}
                    style={{
                      width: "100%",
                      maxWidth: "35px",
                    }}
                  />
                  <CustomTypography
                    variant={"H2"}
                    sx={{
                      p: 0,
                      color: theme.vars.palette.Content.Strong,
                      fontWeight: 700,
                    }}
                  >
                    {"동등한 효과,\n더 적은 부작용"}
                  </CustomTypography>
                </Stack>

                <Stack
                  spacing={3}
                  direction="column"
                  alignItems="start"
                  sx={{ width: "100%", maxWidth: "660px" }}
                >
                  <CustomTypographyHighlight
                    variant={"Subtitle"}
                    sx={{
                      color: theme.vars.palette.Content.Medium,
                      fontWeight: 500,
                      p: 0,
                    }}
                    highlightedTexts={[
                      "증상 호전",
                      "80% 적은 부작용",
                      "가장 먼저 시도",
                    ]}
                    highlightVariant={"Subtitle"}
                    highlightSx={{
                      color: theme.vars.palette.Content.Strong,
                      fontWeight: 700,
                    }}
                  >
                    {"디스턴싱 이용자들은 프로그램 시작 50일 후 34%의 우울, 40%의 불안 증상 호전을 보였습니다.\n\n" +
                      "인지치료는 SSRI 계열의 약물(렉사프로)과 동등한 효과를 내지만, 80% 적은 부작용을 보입니다.¹\n\n" +
                      "영국 국립보건임상연구소(NICE)는 경증∙중등도 수준의 우울증에 있어 인지치료를 가장 먼저 시도해야 할 치료로 권고하고 있습니다."}
                  </CustomTypographyHighlight>
                </Stack>

                <Stack spacing={5}>
                  <img
                    src={mediRef2Img}
                    alt={"empty"}
                    style={{
                      width: "100%",
                      maxWidth: "660px",
                    }}
                  />
                  <CustomTypography
                    variant={"Caption"}
                    sx={{
                      fontWeight: 500,
                      color: theme.vars.palette.Content.Medium,
                      width: "100%",
                      textAlign: "start",
                    }}
                  >
                    {
                      "¹ Hoge et al., 2023 (JAMA Psychiatry), Boschloo et al., 2019 (World Psychiatry)"
                    }
                  </CustomTypography>
                </Stack>
              </Stack>
            }

            {
              <Stack
                py={10}
                spacing={5}
                justifyContent="center"
                alignItems={"center"}
                sx={{
                  backgroundColor: theme.vars.palette.Background.Level3,
                  width: "100%",
                  mx: "auto",
                  px: paddingX,
                }}
              >
                <Stack
                  direction="column"
                  alignItems="start"
                  spacing={"20px"}
                  sx={{ width: "100%", maxWidth: "660px" }}
                >
                  <img
                    src={strongImg}
                    alt={"empty"}
                    style={{
                      width: "100%",
                      maxWidth: "35px",
                    }}
                  />
                  <CustomTypography
                    variant={"H2"}
                    sx={{
                      p: 0,
                      color: theme.vars.palette.Content.Strong,
                      fontWeight: 700,
                    }}
                  >
                    {"힘든 시기,\n다시 겪고 싶지 않다면"}
                  </CustomTypography>
                </Stack>

                <Stack
                  spacing={3}
                  direction="column"
                  alignItems="start"
                  sx={{ width: "100%", maxWidth: "660px" }}
                >
                  <CustomTypographyHighlight
                    variant={"Subtitle"}
                    sx={{
                      color: theme.vars.palette.Content.Medium,
                      fontWeight: 500,
                      p: 0,
                    }}
                    highlightedTexts={["재발률이 30% 낮고", "항우울제를 감량"]}
                    highlightVariant={"Subtitle"}
                    highlightSx={{
                      color: theme.vars.palette.Content.Strong,
                      fontWeight: 700,
                    }}
                  >
                    {"인지치료는 우울증에 있어 일반적인 심리치료(상담, 교육 등)에 비해 재발률이 30% 낮고, 항우울제와는 차이가 없습니다.¹\n\n" +
                      "또한 항우울제를 감량하며 심리치료를 병행하는 것은 항우울제 단독 사용과 동일하게 우울증의 재발을 예방하는 데에 효과적입니다.²"}
                  </CustomTypographyHighlight>
                </Stack>

                <Stack spacing={5} sx={{ width: "100%", maxWidth: "660px" }}>
                  <CustomTypography
                    variant={"Caption"}
                    sx={{
                      fontWeight: 500,
                      color: theme.vars.palette.Content.Medium,
                      width: "100%",
                      textAlign: "start",
                    }}
                  >
                    {"¹ McCartney et al., 2021 (Acta Psychiatr Scand)\n" +
                      "² Breedvelt et al., 2021 (JAMA Psychiatry)"}
                  </CustomTypography>
                </Stack>
              </Stack>
            }

            {
              <Stack
                py={10}
                spacing={5}
                justifyContent="center"
                alignItems={"center"}
                sx={{
                  backgroundColor: theme.vars.palette.Background.Level1,
                  width: "100%",
                  mx: "auto",
                  px: paddingX,
                }}
              >
                <Stack
                  direction="column"
                  alignItems="start"
                  spacing={"20px"}
                  sx={{ width: "100%", maxWidth: "660px" }}
                >
                  <img
                    src={rocketImg}
                    alt={"empty"}
                    style={{
                      width: "100%",
                      maxWidth: "35px",
                    }}
                  />
                  <CustomTypography
                    variant={"H2"}
                    sx={{
                      p: 0,
                      color: theme.vars.palette.Content.Strong,
                      fontWeight: 700,
                    }}
                  >
                    {"오직 전문가와\n함께해야만"}
                  </CustomTypography>
                </Stack>

                <Stack
                  spacing={3}
                  direction="column"
                  alignItems="start"
                  sx={{ width: "100%", maxWidth: "660px" }}
                >
                  <CustomTypographyHighlight
                    variant={"Subtitle"}
                    sx={{
                      color: theme.vars.palette.Content.Medium,
                      fontWeight: 500,
                      p: 0,
                    }}
                    highlightedTexts={["전문가의 도움"]}
                    highlightVariant={"Subtitle"}
                    highlightSx={{
                      color: theme.vars.palette.Content.Strong,
                      fontWeight: 700,
                    }}
                  >
                    {"디지털 인지치료는 전문가의 도움을 받으며 진행할 때 더 좋은 효과를 냅니다.¹\n\n" +
                      "혼자서 진행하는 디지털 치료는 일반적인 약물 치료보다 효과가 떨어졌습니다.²\n\n" +
                      "혼자하려다 한참 돌아갑니다. 하루라도 먼저 변화를 맞이하세요. 디스턴싱 코치진이 기다리고 있습니다."}
                  </CustomTypographyHighlight>
                </Stack>

                <Stack spacing={5}>
                  <img
                    src={mediRef4Img}
                    alt={"empty"}
                    style={{
                      width: "100%",
                      maxWidth: "660px",
                    }}
                  />
                  <CustomTypography
                    variant={"Caption"}
                    sx={{
                      fontWeight: 500,
                      color: theme.vars.palette.Content.Medium,
                      width: "100%",
                      textAlign: "start",
                    }}
                  >
                    {"¹ Heber E, et al., 2021 (JAMA Psychiatry)\n" +
                      "² Papola et al., 2023 (Psychol Med)"}
                  </CustomTypography>
                </Stack>
              </Stack>
            }
            {/*100일간의 집중 코칭*/}
            {isFirstOrder && (
              <Stack
                py={10}
                spacing={5}
                justifyContent="center"
                alignItems={"center"}
                sx={{
                  backgroundColor: theme.vars.palette.Surface.Highlight,
                  width: "100%",
                  px: paddingX,
                  mx: "auto",
                }}
              >
                <Stack
                  spacing={4}
                  alignItems={"center"}
                  justifyContent={"center"}
                >
                  <img
                    src={focusCoachingImg}
                    alt={"empty"}
                    style={{
                      width: "100%",
                      maxWidth: "80px",
                    }}
                  />
                  <CustomTypography
                    variant={"H2"}
                    sx={{
                      color: theme.vars.palette.Content.Negative,
                      fontWeight: 700,
                    }}
                  >
                    {"100일 간의 1:1 코칭"}
                  </CustomTypography>
                </Stack>

                <Stack
                  direction="column"
                  alignItems="start"
                  spacing={2}
                  sx={{ width: "100%", maxWidth: "660px" }}
                >
                  <CustomTypographyHighlight
                    variant={"Subtitle"}
                    sx={{
                      color: theme.vars.palette.Content.SoftHighlight,
                      fontWeight: 500,
                    }}
                    highlightedText={"맞춤형 프로그램"}
                    highlightVariant={"Subtitle"}
                    highlightSx={{
                      color: theme.vars.palette.Content.Negative,
                      fontWeight: 700,
                    }}
                  >
                    {"프로그램이 시작되면 전담 코치가 배정됩니다.\n\n" +
                      "코치는 심리학 분야에서 석사 이상의 학위를 갖추고, 공인자격증을 갖춘 심리전문가들이죠.\n\n" +
                      "매일 코치와 함께 맞춤형 프로그램으로 디스턴싱 기술을 익히세요."}
                  </CustomTypographyHighlight>
                </Stack>
                <img
                  src={taskUpdateMechanism}
                  alt={"empty"}
                  style={{
                    width: "100%",
                    maxWidth: "321px",
                  }}
                />
              </Stack>
            )}
            {/*영역5: 100일로 완성되는*/}
            {isFirstOrder && (
              <Stack
                py={10}
                spacing={5}
                justifyContent="center"
                alignItems={"center"}
                sx={{
                  backgroundColor: theme.vars.palette.Background.Level1,
                  width: "100%",
                  mx: "auto",
                  px: paddingX,
                }}
              >
                <Stack
                  direction="column"
                  alignItems="start"
                  spacing={"20px"}
                  sx={{ width: "100%", maxWidth: "660px" }}
                >
                  <img
                    src={calendarImg}
                    alt={"empty"}
                    style={{
                      width: "100%",
                      maxWidth: "35px",
                    }}
                  />
                  <Typography
                    variant={"H2"}
                    sx={{
                      p: 0,
                      color: theme.vars.palette.Content.Strong,
                      fontWeight: 700,
                    }}
                  >
                    {"100일로 완성되는\n지속가능한 변화"}
                  </Typography>
                </Stack>

                <Stack
                  spacing={5}
                  justifyContent="center"
                  alignItems={"center"}
                  sx={{ width: "100%", maxWidth: "660px" }}
                >
                  <Stack spacing={3}>
                    <CustomTypographyHighlight
                      variant={"Subtitle"}
                      sx={{
                        color: theme.vars.palette.Content.Medium,
                        fontWeight: 500,
                        p: 0,
                      }}
                      highlightedText={"디스턴싱 기술이 체화"}
                      highlightVariant={"Subtitle"}
                      highlightSx={{
                        color: theme.vars.palette.Content.Strong,
                        fontWeight: 700,
                      }}
                    >
                      {
                        "디스턴싱은 지속가능한 변화를 위해 설계되었습니다. 이 지속가능성은 디스턴싱 기술이 체화되어 일상에서 자연스럽게 실천될 때에 완성됩니다."
                      }
                    </CustomTypographyHighlight>
                    <CustomTypographyHighlight
                      variant={"Subtitle"}
                      sx={{
                        color: theme.vars.palette.Content.Medium,
                        fontWeight: 500,
                        p: 0,
                      }}
                      highlightedText={"필요한 기간은 100일"}
                      highlightVariant={"Subtitle"}
                      highlightSx={{
                        color: theme.vars.palette.Content.Strong,
                        fontWeight: 700,
                      }}
                    >
                      {
                        "디스턴싱에서 성공적인 효과를 보인 80,000명의 데이터를 분석한 결과 안정적인 정착을 위해 필요한 기간은 100일이었습니다."
                      }
                    </CustomTypographyHighlight>
                    <CustomTypographyHighlight
                      variant={"Subtitle"}
                      sx={{
                        color: theme.vars.palette.Content.Medium,
                        fontWeight: 500,
                        p: 0,
                      }}
                      highlightedText={
                        "디스턴싱은 번쩍이는 요행을 바라지 않습니다."
                      }
                      highlightVariant={"Subtitle"}
                      highlightSx={{
                        color: theme.vars.palette.Content.Strong,
                        fontWeight: 700,
                      }}
                    >
                      {
                        "디스턴싱은 번쩍이는 요행을 바라지 않습니다. 디스턴싱은 평생에 걸친 장기적인 변화를 만듭니다."
                      }
                    </CustomTypographyHighlight>
                  </Stack>
                  <img
                    src={chart100Img}
                    alt={"empty"}
                    style={{
                      width: "100%",
                      maxWidth: "321px",
                    }}
                  />
                </Stack>
              </Stack>
            )}
            <Stack
              py={10}
              spacing={2}
              justifyContent="center"
              alignItems="center"
              sx={{
                backgroundColor: theme.vars.palette.Background.Level3,
                width: "100%",
                maxWidth: "660px",
                mx: "auto",
                position: "relative",
                overflow: "visible",
                pb: "50px",
              }}
            >
              <Stack spacing={"44px"} alignItems={"center"}>
                <CustomTypography
                  variant={"Body"}
                  sx={{
                    color: theme.vars.palette.Content.Medium,
                    fontWeight: 510,
                  }}
                >
                  {"Supported by"}
                </CustomTypography>
                <img
                  src={sponsorImg}
                  alt={"empty"}
                  style={{
                    width: "100%",
                    maxWidth: "300px",
                    paddingBottom: "60px",
                  }}
                />
              </Stack>
              <CustomTypography
                pt={5}
                variant="Body"
                sx={{
                  color: theme.vars.palette.Content.Medium,
                  fontWeight: 500,
                }}
              >
                PC, 모바일 완벽 지원
              </CustomTypography>

              <Box
                sx={{
                  mr: "-60px",
                  mt: 2,
                  width: "90%",
                  alignSelf: "flex-end",
                }}
              >
                <img
                  src={pcAndMobileImg}
                  alt="pc_and_mobile"
                  style={{
                    width: "100%",
                    height: "auto",
                  }}
                />
              </Box>
            </Stack>

            <Stack
              py={10}
              spacing={5}
              justifyContent="center"
              alignItems={"center"}
              sx={{
                backgroundColor: theme.vars.palette.Content.Strong,
                width: "100%",
                px: paddingX,
                mx: "auto",
              }}
            >
              <Stack
                spacing={4}
                alignItems={"center"}
                justifyContent={"center"}
              >
                <CustomTypography
                  variant={"H2"}
                  textAlign={"center"}
                  sx={{
                    color: theme.vars.palette.Content.SoftHighlight,
                    fontWeight: 700,
                  }}
                >
                  {"매일의 루틴이\n만드는 변화"}
                </CustomTypography>
              </Stack>

              <Stack
                spacing={3}
                alignItems={"start"}
                sx={{ width: "100%", maxWidth: "660px" }}
              >
                <CustomTypography
                  variant={"Subtitle"}
                  sx={{
                    p: 0,
                    fontWeight: 700,
                    color: theme.vars.palette.Content.Negative,
                  }}
                >
                  {"코칭 시간"}
                </CustomTypography>
                <CustomTypography
                  variant={"Subtitle"}
                  sx={{
                    p: 0,
                    fontWeight: 500,
                    color: theme.vars.palette.Content.SuperWeak,
                  }}
                >
                  {
                    "코치는 평일 오전 8시부터 오후 7시 사이에 코칭을 진행합니다."
                  }
                </CustomTypography>

                <img
                  src={coachingTimeImg}
                  alt={"empty"}
                  style={{
                    width: "100%",
                    maxWidth: isInappWebview ? "660px" : "360px",
                    display: "block",
                    margin: "0 auto",
                    marginTop: "24px",
                  }}
                />
              </Stack>

              <img
                src={dividerImg}
                alt={"empty"}
                style={{
                  width: "100%",
                  maxWidth: "660px",
                }}
              />

              <Stack
                spacing={3}
                alignItems={"start"}
                sx={{ width: "100%", maxWidth: "660px" }}
              >
                <CustomTypography
                  variant={"Subtitle"}
                  sx={{
                    p: 0,
                    fontWeight: 700,
                    color: theme.vars.palette.Content.Negative,
                  }}
                >
                  {"코칭 루틴"}
                </CustomTypography>
                <CustomTypography
                  variant={"Subtitle"}
                  sx={{
                    p: 0,
                    fontWeight: 500,
                    color: theme.vars.palette.Content.SuperWeak,
                  }}
                >
                  {
                    "오후 3시 이전에 주어진 모든 활동을 완료하면, 당일에 피드백과 함께 다음 활동지가 제공됩니다."
                  }
                </CustomTypography>

                <img
                  src={coachingRoutine1Img}
                  alt={"empty"}
                  style={{
                    width: "100%",
                    maxWidth: isInappWebview ? "660px" : "360px",
                    display: "block",
                    margin: "0 auto",
                    marginTop: "24px",
                  }}
                />
              </Stack>

              <Stack
                spacing={3}
                alignItems={"start"}
                sx={{ width: "100%", maxWidth: "660px" }}
              >
                <CustomTypography
                  variant={"Subtitle"}
                  sx={{
                    p: 0,
                    fontWeight: 500,
                    color: theme.vars.palette.Content.SuperWeak,
                  }}
                >
                  {"오후 3시 이후에 완료하면, 다음 날 업데이트가 이루어집니다."}
                </CustomTypography>
                <img
                  src={coachingRoutine2Img}
                  alt={"empty"}
                  style={{
                    width: "100%",
                    maxWidth: isInappWebview ? "660px" : "360px",
                    display: "block",
                    margin: "0 auto",
                    marginTop: "24px",
                  }}
                />
                <img
                  src={coachingRoutine3Img}
                  alt={"empty"}
                  style={{
                    width: "100%",
                    maxWidth: isInappWebview ? "660px" : "360px",
                    display: "block",
                    margin: "0 auto",
                    marginTop: "24px",
                  }}
                />
              </Stack>
              <Stack
                spacing={3}
                alignItems={"start"}
                sx={{ width: "100%", maxWidth: "660px" }}
              >
                <CustomTypography
                  variant={"Subtitle"}
                  sx={{
                    p: 0,
                    fontWeight: 500,
                    color: theme.vars.palette.Content.SuperWeak,
                  }}
                >
                  {isNoStarterModel || isSinglePlanModel
                    ? "즉, 완료 시각과 관계없이 활동을 매일 완료하면 업데이트도 매일 받을 수 있습니다."
                    : "즉, 완료 시각과 관계없이 활동을 매일 완료하면 업데이트도 매일 받을 수 있습니다. (단, 스타터 플랜은 주 2회 제공)"}
                </CustomTypography>
              </Stack>
              <img
                src={dividerImg}
                alt={"empty"}
                style={{
                  width: "100%",
                  maxWidth: "660px",
                }}
              />

              <Stack
                spacing={3}
                alignItems={"start"}
                sx={{ width: "100%", maxWidth: "660px" }}
              >
                <CustomTypography
                  variant={"Subtitle"}
                  sx={{
                    p: 0,
                    fontWeight: 700,
                    color: theme.vars.palette.Content.Negative,
                  }}
                >
                  {isSinglePlanModel ? "코칭 횟수" : "플랜별 코칭 횟수"}
                </CustomTypography>
                {!isSinglePlanModel && (
                  <>
                    <CustomTypographyHighlight
                      variant={"Subtitle"}
                      sx={{
                        p: 0,
                        fontWeight: 500,
                        color: theme.vars.palette.Content.SuperWeak,
                      }}
                      highlightedText={"마스터"}
                      highlightVariant={"Subtitle"}
                      highlightSx={{
                        color: theme.vars.palette.Content.Negative,
                        fontWeight: 700,
                      }}
                    >
                      {
                        "마스터: 오후 3시 이전에 두 차례 활동을 완료한 경우, 당일 최대 2회까지 업데이트가 가능합니다."
                      }
                    </CustomTypographyHighlight>

                    <img
                      src={coachingCount1Img}
                      alt={"empty"}
                      style={{
                        width: "100%",
                        maxWidth: isInappWebview ? "660px" : "360px",
                        display: "block",
                        margin: "0 auto",
                        marginTop: "24px",
                      }}
                    />
                  </>
                )}
              </Stack>

              <Stack
                spacing={3}
                alignItems={"start"}
                sx={{ width: "100%", maxWidth: "660px" }}
              >
                {isSinglePlanModel ? (
                  <CustomTypography
                    variant={"Subtitle"}
                    sx={{
                      p: 0,
                      fontWeight: 500,
                      color: theme.vars.palette.Content.SuperWeak,
                    }}
                  >
                    {
                      "일 최대 1회 업데이트되며, 오후 3시 이전에 완료된 두 번째 활동에 대해서는 다음 날 업데이트가 제공됩니다."
                    }
                  </CustomTypography>
                ) : (
                  <CustomTypographyHighlight
                    variant={"Subtitle"}
                    sx={{
                      p: 0,
                      fontWeight: 500,
                      color: theme.vars.palette.Content.SuperWeak,
                    }}
                    highlightedText={"챌린저"}
                    highlightVariant={"Subtitle"}
                    highlightSx={{
                      color: theme.vars.palette.Content.Negative,
                      fontWeight: 700,
                    }}
                  >
                    {
                      "챌린저: 일 최대 1회 업데이트로 제한되며, 오후 3시 이전에 완료된 두 번째 활동에 대해서는 다음 날 업데이트가 제공됩니다."
                    }
                  </CustomTypographyHighlight>
                )}
                <img
                  src={coachingCount2Img}
                  alt={"empty"}
                  style={{
                    width: "100%",
                    maxWidth: isInappWebview ? "660px" : "360px",
                    display: "block",
                    margin: "0 auto",
                    marginTop: "24px",
                  }}
                />
                <img
                  src={coachingCount3Img}
                  alt={"empty"}
                  style={{
                    width: "100%",
                    maxWidth: isInappWebview ? "660px" : "360px",
                    display: "block",
                    margin: "0 auto",
                    marginTop: "24px",
                  }}
                />
              </Stack>
              {!isNoStarterModel && !isSinglePlanModel && (
                <Stack
                  spacing={3}
                  alignItems={"start"}
                  sx={{ width: "100%", maxWidth: "660px" }}
                >
                  <CustomTypographyHighlight
                    variant={"Subtitle"}
                    sx={{
                      p: 0,
                      fontWeight: 500,
                      color: theme.vars.palette.Content.SuperWeak,
                    }}
                    highlightedText={"스타터"}
                    highlightVariant={"Subtitle"}
                    highlightSx={{
                      color: theme.vars.palette.Content.Negative,
                      fontWeight: 700,
                    }}
                  >
                    {
                      "스타터: 일 최대 업데이트 횟수는 1회로 챌린저 플랜과 동일하지만, 주 최대 2회까지 업데이트가 제공됩니다."
                    }
                  </CustomTypographyHighlight>
                </Stack>
              )}
            </Stack>
            {isFirstOrder && (
              <Stack
                py={10}
                justifyContent="center"
                alignItems={"center"}
                sx={{
                  backgroundColor: theme.vars.palette.Background.Level3,
                  width: "100%",
                  mx: "auto",
                  px: paddingX,
                }}
              >
                <Stack spacing={5} sx={{ width: "100%", maxWidth: "660px" }}>
                  <Typography
                    variant={"H2"}
                    sx={{
                      p: 0,
                      color: theme.vars.palette.Content.Strong,
                      fontWeight: 700,
                    }}
                  >
                    {"자주 묻는 질문"}
                  </Typography>
                  {isSinglePlanModel ? (
                    <FAQRefundTypeDSinglePricingModel />
                  ) : isNoStarterModel ? (
                    user?.refundPolicyType === "c" ? (
                      <FAQTypeCPricingModelEG />
                    ) : (
                      <FAQRefundTypeDPricingModelEG />
                    )
                  ) : user?.refundPolicyType === "c" ? (
                    <FAQTypeC />
                  ) : user?.refundPolicyType === "b" ? (
                    <FAQRefundTypeB />
                  ) : (
                    <FAQDefault paymentPageType={user?.paymentPageType} />
                  )}
                </Stack>
              </Stack>
            )}
            <Stack
              spacing={2}
              justifyContent="center"
              alignItems={"center"}
              sx={{
                backgroundColor: theme.vars.palette.Background.Level3,
                width: "100%",
                maxWidth: "660px",
                mx: "auto",
                px: paddingX,
                pt: isFirstOrder ? 0 : 9,
              }}
            >
              <Stack direction={"row"} spacing={3}>
                {user && <UserInfo user={user} />}
                {user && <CxButton type="link" />}
              </Stack>
              <Stack direction={"row"} spacing={3}>
                <LinkV2 text={"사업자 정보"} url={companyInfoUrl} />
                <LinkV2 text={"개인정보 처리방침"} url={privacyPolicyUrl} />
                <LinkV2 text={"서비스 이용약관"} url={servicePolicyUrl} />
              </Stack>
            </Stack>
            <Box>
              <FloatingArea
                fullWidth={fullWidth}
                ref={floatingAreaRef}
                paddingBottom={"12px"}
              >
                <Stack
                  spacing={1.5}
                  sx={{ width: "100%" }}
                  alignItems="center"
                  justifyContent={"space-between"}
                >
                  {/* 선택된 플랜에 따른 문구 동기화 */}
                  <CustomTypography
                    variant={"Caption"}
                    sx={{
                      p: 0,
                      fontWeight: 500,
                      color: theme.vars.palette.Content.Medium,
                      textAlign: "center",
                    }}
                  >
                    {isFirstOrder
                      ? `100일 완성 ${formatCurrency(selectedPlanObj.price)}원\u00A0\u00A0|\u00A0\u00A0${guaranteeLabel}`
                      : `100일 ${formatCurrency(selectedPlanObj.price)}원\u00A0\u00A0|\u00A0\u00A0언제든 환불 가능`}
                  </CustomTypography>

                  <Stack
                    spacing={"16px"}
                    sx={{ width: "100%" }}
                    alignItems="center"
                    justifyContent={"space-between"}
                  >
                    {paymentOrderState && (
                      <PaymentButtonWithPlanSelection
                        selectedPlan={effectiveSelectedPlan}
                      />
                    )}
                  </Stack>
                </Stack>
              </FloatingArea>
            </Box>
          </Stack>
        )}
        <CustomModal
          open={infoModalOpen}
          onClose={() => setInfoModalOpen(false)}
          title={infoModal.title}
          subtitle={infoModal.subtitle}
          actions={[
            {
              label: infoModal.action,
              onClick: () => setInfoModalOpen(false),
              variant: "Solid",
            },
          ]}
        />
      </ContentTemplateV2>
    </Stack>
  );
}
